import React, {
  useState, useRef, useMemo, useEffect,
} from 'react';
import { Grid } from '@material-ui/core';
import { TextLight } from 'components/Texts';
import { Hollow } from 'components/Buttons';
import { colors, Div, weight } from 'globalStyle';
import { getAmount } from 'util/getAmount';
import { useFormProps, useUser } from 'hooks';
import { formatNumber } from 'util/formatNumber';
import useUserFunctions from 'hooks/user/useUserFunctions';
import { capitalizeFirstLetterString } from 'util/capitalizeFirstLetter';
import { ColorBackgroundTitle } from 'components/Modals/ColorBackgroundTitle';
import { calculateDifferenceBetweenDates } from 'util/calculateDifferenceBetweenDates';
import { payFrequency, projectStateType, userType } from 'types';
import { Loading } from 'components/Loading';
import { InputFormatNumber } from '../../../../components/Form/InputFormatNumber';
import { investmentConditions } from './investmentConditions';
import PaymentDetails from './PaymentDetails';
import { handleSubmit } from './handleSubmit';

export default function InvestorSimulator({
  handleClose,
  project,
  handleInvert,
  withForm = true,
}) {
  const { formProps } = useFormProps({ errorToolTip: true, required: true });
  const [dataSimulator, setDataSimulator] = useState({
    NroLastQuota: 0,
    fixedFee: 0,
    amountTotal: 0,
    amountEarn: 0,
    lastQuota: 0,
  });
  const { isLogin, type } = useUser();
  const { isVIP } = useUserFunctions();
  const [isCalculating, setIsCalculating] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [amount, setAmount] = useState(0);
  const [paymentDetails, setPaymentDetails] = useState(false);
  const showInverButton = useMemo(
    () => project.status === projectStateType.OPEN
      && (!isLogin || (type === userType.USER && !isVIP)),
    [project, isLogin, type],
  );

  const ref = useRef(null);
  const conditions = investmentConditions(project);
  const time = calculateDifferenceBetweenDates(project.endDate);

  const handleWarranty = (isWarranty, warrantyCompanyName, warranty) => {
    if (isWarranty) {
      return ` ${warrantyCompanyName} ${(warranty * 100).toFixed()}%`;
    }
    return 'Sin garantía';
  };

  const handleScroll = () => {
    ref?.current?.scrollIntoView({
      height: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (!withForm) {
      handleSubmit(
        { amount: project.amount },
        setAmount,
        formProps,
        project,
        setDataSimulator,
        setShowMessage,
        setIsCalculating,
        handleScroll,
        withForm,
      );
    }
  }, []);

  const onSubmit = (form) => {
    handleSubmit(
      form,
      setAmount,
      formProps,
      project,
      setDataSimulator,
      setShowMessage,
      setIsCalculating,
      handleScroll,
      withForm,
    );
  };
  const ComponentTitle = () => (
    <Div padding="20px 0 10px">
      <TextLight
        color={colors.WHITE}
        align="center"
        size={14}
        weight={weight.BOLD}
      >
        Simulador
      </TextLight>
      <TextLight
        color={colors.WHITE}
        align="center"
        weight={weight.BOLD}
        size={18}
      >
        {capitalizeFirstLetterString(project?.projectName)}
      </TextLight>
      <TextLight color={colors.WHITE} align="center" size={14}>
        {`Proyecto de ${capitalizeFirstLetterString(project?.companyName)}`}
      </TextLight>
    </Div>
  );

  const Item = ({ name, value }) => (
    <Div isFlex direction="column" width="95%" p="10px 5px 10px 20px">
      <TextLight
        color={colors.primary}
        size={13}
        weight={weight.MEDIUM}
        align="start"
        mb={5}
      >
        {name}
      </TextLight>
      <TextLight size={14} weight={weight.BOLD} align="start">
        {value}
      </TextLight>
    </Div>
  );

  const ContainerTypeSimulator = () => {
    const bankVisionSimulator = [
      {
        name: 'Abono de capital:',
        value: formatNumber(dataSimulator.capital),
      },
      {
        name: 'Pago de intereses:',
        value: formatNumber(dataSimulator.interes),
      },
      {
        name: capitalizeFirstLetterString(
          `Cuota ${
            payFrequency[
              project?.frequencyInterest || payFrequency[project?.frequency]
            ]
          }:`,
        ),
        value: formatNumber(dataSimulator.fixedFee),
        detail: '*Capital + Intereses',
      },
    ];
    const bulletSimulator = [
      {
        name: project?.gracePeriod
          ? `De la cuota ${project?.gracePeriod + 1} a la ${
            project.paymentPeriods - 1
          } recibes:`
          : capitalizeFirstLetterString(
            `Cuota ${
              payFrequency[
                project?.frequencyInterest || payFrequency[project?.frequency]
              ]
            }:`,
          ),
        value: formatNumber(dataSimulator.fixedFee),
        detail: '*Intereses',
      },
      {
        name: 'Última cuota:',
        value: formatNumber(dataSimulator.lastQuota),
        detail: '*Capital + Intereses',
      },
    ];
    if (project?.simulatorType === 'BANK_VISION') {
      return (
        <Div isFlex direction="column" style={{ rowGap: '10px' }}>
          {bankVisionSimulator.map(({ name, value, detail }) => (
            <Div isFlex justify="flex-start" width="100%">
              <TextLight size={14} width="30%" align="start">
                {name}
              </TextLight>
              <TextLight
                size={14}
                weight={weight.BOLD}
                width="44%"
                align="start"
              >
                {value}
              </TextLight>
              {detail && (
                <TextLight
                  color={colors.primary}
                  size={12}
                  weight={weight.MEDIUM}
                  width="26%"
                  align="start"
                  style={{ fontStyle: 'italic' }}
                >
                  {detail}
                </TextLight>
              )}
            </Div>
          ))}
        </Div>
      );
    }
    return (
      <Div isFlex direction="column" style={{ rowGap: '10px' }}>
        {bulletSimulator.map(({ name, value, detail }) => (
          <Div isFlex justify="flex-start" width="100%">
            <TextLight size={14} align="start" width="74%">
              {name}
              <span style={{ fontWeight: weight.BOLD, marginLeft: '3px' }}>
                {value}
              </span>
            </TextLight>
            {detail && (
              <TextLight
                color={colors.primary}
                size={12}
                weight={weight.MEDIUM}
                width="26%"
                align="start"
                style={{ fontStyle: 'italic' }}
              >
                {detail}
              </TextLight>
            )}
          </Div>
        ))}
      </Div>
    );
  };

  const handleBackPaymentDetails = () => {
    setPaymentDetails(false);
  };
  if (paymentDetails) {
    return (
      <PaymentDetails
        simulatorData={dataSimulator.data}
        investmentInfo={dataSimulator}
        handleClose={handleClose}
        handleBack={handleBackPaymentDetails}
        project={project}
        amount={amount}
      />
    );
  }

  return (
    <ColorBackgroundTitle
      isOpen
      handleBack={() => {
        handleClose();
      }}
      handleClose={() => {
        handleClose();
      }}
      title={<ComponentTitle />}
    >
      <Div p="0 30px">
        <Div isFlex>
          <TextLight size={13}>
            Meta:
            <span
              style={{
                fontWeight: weight.BOLD,
                margin: '0 3px',
                fontSize: '14px',
              }}
            >
              {formatNumber(project?.maxAmount)}
            </span>
          </TextLight>
          {time !== 'Tiempo expirado' && (
            <TextLight size={13}>
              Quedan:
              <span
                style={{
                  fontWeight: weight.BOLD,
                  margin: '0 3px',
                  fontSize: '14px',
                }}
              >
                {time}
              </span>
            </TextLight>
          )}

          <TextLight size={13}>
            Inversionistas:
            <span
              style={{
                fontWeight: weight.BOLD,
                margin: '0 3px',
                fontSize: '14px',
              }}
            >
              {project?.currentInvestorsAmount}
            </span>
          </TextLight>
        </Div>
        <Div mt={20} mb={20}>
          <TextLight
            color={colors.primary}
            size={16}
            weight={weight.BOLD}
            mb={20}
          >
            Condiciones de inversión
          </TextLight>

          <Grid container>
            {conditions?.map((item) => (
              <Grid item xs={4} style={{ ...item?.style }}>
                <Item {...item} />
              </Grid>
            ))}
          </Grid>

          <TextLight color={colors.primary} size={14} mt={20}>
            Garantía
            <span
              style={{
                color: colors.GRAY_1,
                fontWeight: weight.BOLD,
                marginLeft: '10px',
              }}
            >
              {handleWarranty(
                project.isWarranty,
                project.warrantyCompanyName,
                project.warranty,
              )}
            </span>
          </TextLight>
        </Div>
        {withForm && (
          <form onSubmit={formProps.handleSubmit(onSubmit)}>
            <InputFormatNumber
              label="¿Cuánto quieres invertir?"
              name="amount"
              placeholder={`Desde ${formatNumber(project?.minInvestmentAmount, 0, project?.currencyIso)}`}
              validate={{
                min: (value) => getAmount(value) >= project?.minInvestmentAmount
                  || `El monto ingresado debe ser mayor o igual a ${formatNumber(project?.minInvestmentAmount, 0, project?.currencyIso)}`,
                max: (value) => getAmount(value) < project?.maxAmount
                  || 'El monto ingresado debe ser menor que el máximo de recaudo',
                divisible: (value) => getAmount(value) % project?.minInvestmentAmount === 0
                  || `El monto ingresado debe ser multiplo de ${formatNumber(project?.minInvestmentAmount, 0, project?.currencyIso)}`,
              }}
              defaultValue={amount !== 0 && amount}
              styles={{ textAlign: 'start', width: '100%' }}
              {...formProps}
            />

            <Hollow
              isSubmit
              onColorType="YELLOW"
              color={colors.BLACK}
              padding="0 12px"
              isLoading={isCalculating}
              style={{ width: '220px', height: '45px' }}
            >
              Simular
            </Hollow>
          </form>
        ) }
        {!withForm && isCalculating && (
          <Div isCenter margin="10px 0px 0px">
            <Loading />
          </Div>
        )}

        {showMessage && (
          <Div ref={ref}>
            <Div
              backgroundColor={colors.background.GRAY_3}
              p="20px"
              radius="15px"
              mt={20}
            >
              <TextLight size={14} weight={weight.BOLD} align="center" mb={20}>
                Valores aproximados de inversión
              </TextLight>
              <Div
                isFlex
                justify="space-between"
                style={{
                  textAlign: 'start',
                  borderBottom: `2px solid ${colors.input.inactive}`,
                  paddingBottom: '10px',
                }}
                mb={10}
              >
                <Div width="50%">
                  <TextLight size={14}>
                    Inversión inicial:
                    <span style={{ fontWeight: weight.BOLD, margin: '0 5px' }}>
                      {formatNumber(amount)}
                    </span>
                  </TextLight>
                </Div>
                <Div width="50%">
                  <TextLight size={14}>
                    Recibes en total:
                    <span style={{ fontWeight: weight.BOLD, margin: '0 5px' }}>
                      {formatNumber(dataSimulator.amountTotal)}
                    </span>
                  </TextLight>
                </Div>
              </Div>
              <ContainerTypeSimulator />
            </Div>
            <Div
              backgroundColor={colors.secondary}
              p="5px 10px"
              radius="10px"
              margin="20px 0"
            >
              <TextLight size={13} weight={weight.MEDIUM} align="center">
                * Recuerda que estos valores son aproximados y pueden variar en
                un proceso real de financiación colaborativa.
              </TextLight>
            </Div>
            <Div isFlex justify={showInverButton && 'space-between'}>
              <Hollow
                isSubmit
                onClick={() => setPaymentDetails(true)}
                padding="12px 0"
                size="14px"
                onColorType="white"
                color={colors.primary}
                style={{ borderRadius: '30px', width: '250px', height: '45px' }}
              >
                Ver detalle de pagos
              </Hollow>
              {showInverButton && (
                <Hollow
                  isSubmit
                  onColorType="primary"
                  padding="12px 0"
                  size="14px"
                  onClick={() => {
                    handleInvert();
                  }}
                  style={{
                    borderRadius: '30px',
                    width: '250px',
                    height: '45px',
                  }}
                >
                  Invertir
                </Hollow>
              )}
            </Div>
          </Div>
        )}
      </Div>
    </ColorBackgroundTitle>
  );
}
